import { num } from '../../scene/component-types.js';
import type { SettingsSection } from '../settings-types.js';
import { WEBSITE } from '../../../lib/edition.js';

/**
 * `settings.network` (prism_core::net_config::NetworkSettings), split into
 * four cards that all edit the same object. Defaults mirror
 * `NetworkSettings::default()`, ranges mirror validate_network.rs
 * (exclusive minimums use the smallest sensible value). See
 * engine/docs/systems/networking-options.md.
 */

const basics: SettingsSection = {
  key: 'network',
  label: 'Network',
  icon: 'globe',
  order: 60,
  hint: 'Protocol, tick rate, players, port, dedicated-server scene',
  fields: [
    {
      key: 'protocol',
      label: 'Protocol version',
      type: num(1, { min: 0, max: 4294967295, integer: true }),
      read: () => 1,
      hint: 'bump when the network protocol or content changes; clients with another value are refused',
    },
    {
      key: 'tickRate',
      label: 'Tick rate',
      type: num(1, { min: 1, max: 240 }),
      read: () => 30,
      hint: 'server ticks per second (input processing, snapshots); clients send input at this rate',
    },
    {
      key: 'maxPlayers',
      label: 'Max players',
      type: num(1, { min: 1, max: 1024, integer: true }),
      read: () => 16,
      hint: 'peers the server accepts (the max_players host option overrides)',
    },
    {
      key: 'port',
      label: 'Port',
      type: num(1, { min: 1, max: 65535, integer: true }),
      read: () => 7777,
      hint: 'default port for open_to_network, connect and prism serve',
    },
    {
      key: 'serverScene',
      label: 'Server scene',
      type: { kind: 'path', suffix: '.scene.json' },
      hint: 'scene a dedicated server (prism serve) loads; empty = the start scene',
    },
    {
      key: 'timeout',
      label: 'Timeout (s)',
      type: num(1, { min: 1, max: 3600 }),
      read: () => 10,
      hint: 'seconds of silence before a connection is dropped (also the connect timeout)',
    },
  ],
};

const replication: SettingsSection = {
  key: 'network',
  label: 'Network replication',
  icon: 'refresh',
  order: 61,
  hint: 'Update rates, interpolation, state packets, world edits',
  fields: [
    {
      key: 'defaultReplicateRate',
      label: 'Default replicate rate (Hz)',
      type: num(1, { min: 0.1, max: 240 }),
      read: () => 20,
      hint: 'replicate.rate of entities that set none (updates per second)',
    },
    {
      key: 'interpolationDelay',
      label: 'Interpolation delay (s)',
      type: num(0.01, { min: 0, max: 2 }),
      read: () => 0.1,
      hint: 'default seconds interpolated entities are rendered in the past (replicate.interpolationDelay overrides)',
    },
    {
      key: 'refreshInterval',
      label: 'Refresh interval (s)',
      type: num(0.1, { min: 0.05, max: 3600 }),
      read: () => 1,
      hint: 'unchanged transforms are re-sent this often (recovers lost unreliable updates)',
    },
    {
      key: 'stateBudgetBytes',
      label: 'State budget (bytes)',
      type: num(50, { min: 64, max: 65000, integer: true }),
      read: () => 1000,
      hint: 'transform bytes per unreliable state packet; keep at most mtu − 160 (larger values are clamped)',
    },
    {
      key: 'inputRedundancy',
      label: 'Input redundancy',
      type: num(1, { min: 1, max: 8, integer: true }),
      read: () => 3,
      hint: 'input commands repeated in every input packet (protects taps from packet loss)',
    },
    {
      key: 'replicateTerrainEdits',
      label: 'Replicate terrain edits',
      type: { kind: 'bool' },
      read: () => false,
      hint: 'send every server terrain edit to clients as a "terrain" world edit (including history for late joiners)',
    },
  ],
};

const precision: SettingsSection = {
  key: 'network',
  label: 'Network precision',
  icon: 'grid',
  order: 62,
  hint: 'Quantization of replicated transforms',
  fields: [
    {
      key: 'positionPrecision',
      label: 'Position precision',
      type: num(0.001, { min: 0.000001, max: 1 }),
      read: () => 0.001,
      hint: 'position quantization step in world units (> 0)',
    },
    {
      key: 'scalePrecision',
      label: 'Scale precision',
      type: num(0.001, { min: 0.000001, max: 1 }),
      read: () => 0.001,
      hint: 'scale quantization step (> 0)',
    },
    {
      key: 'rotationPrecision',
      label: 'Rotation precision (°)',
      type: num(0.1, { min: 0, max: 90 }),
      read: () => 0.5,
      hint: 'degrees a rotation must change before it is sent again (rotations are encoded at ~0.003°)',
    },
  ],
};

const transport: SettingsSection = {
  key: 'network',
  label: 'Network transport',
  icon: 'link',
  order: 63,
  hint: 'Packet size, reliable windows, bandwidth',
  fields: [
    {
      key: 'mtu',
      label: 'MTU (bytes)',
      type: num(1, { min: 576, max: 65000, integer: true }),
      read: () => 1200,
      hint: 'largest UDP packet; above ~1400 only on LANs (packets may be fragmented or dropped on the internet)',
    },
    {
      key: 'reliableSendWindow',
      label: 'Reliable send window',
      type: num(64, { min: 1, max: 32768, integer: true }),
      read: () => 4096,
      hint: 'reliable messages in flight past the oldest unacknowledged one',
    },
    {
      key: 'reliableReceiveWindow',
      label: 'Reliable receive window',
      type: num(64, { min: 1, max: 32768, integer: true }),
      read: () => 8192,
      hint: 'out-of-order reliable messages buffered on receive',
    },
    {
      key: 'maxBytesPerSecond',
      label: 'Max bytes / second',
      type: num(100000, { min: 1001 }),
      read: () => 8000000,
      hint: 'outgoing pacing limit per connection (> 1000)',
    },
  ],
};

/**
 * The website's games play online through the Prism relay only: no port, no
 * dedicated-server scene and no UDP transport tuning (the relay owns those);
 * relayed games hold at most 8 players.
 */
const websiteBasics: SettingsSection = {
  ...basics,
  hint: 'Protocol, tick rate, players (join codes: up to 8), timeout',
  fields: basics.fields.filter((f) => f.key !== 'port' && f.key !== 'serverScene'),
};

export default WEBSITE ? [websiteBasics, replication, precision] : [basics, replication, precision, transport];
